import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { WebSocketService } from '@qtek/core/websockets-core';
import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

import { OrganizationsActions } from './organizations.actions';
import { OrganizationsService } from './organizations.service';

@Injectable()
export class OrganizationsEffects {
  connectOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.subscribeOrganizations),
      switchMap(action => {
        this.organizationsService.getOrganizations({
          mysid: action.mysid,
          prms: action.params,
          s: 'model',
          view: 'admin',
        });

        return this.webSocketService.getWebSocket$().pipe(
          filter(
            (message: any) =>
              message.ent === 'acn' &&
              message.mysid === action.mysid &&
              (message.op === 'query' || message.op === 'fast_query')
          ),
          take(1),
          takeUntil(
            this.actions$.pipe(
              ofType(OrganizationsActions.unsubscribeOrganizations),
              filter(unsubAction => action?.mysid === unsubAction?.mysid)
            )
          ),
          map(({ res, sts }: any) =>
            res || sts?.meta || res === undefined
              ? OrganizationsActions.getOrganizationsSuccess({
                  payload: Array.isArray(res) ? res : [],
                  meta: sts?.meta,
                })
              : OrganizationsActions.getOrganizationsFailure({
                  payload: sts,
                })
          ),
          catchError(({ sts }: any) =>
            of(OrganizationsActions.getOrganizationsFailure({ payload: sts }))
          )
        );
      })
    )
  );

  getOrganizationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.getOrganizationDetails),
      mergeMap(({ id }) => {
        const mysid = `org-details-${id}`;
        const messageFilter = (message: any) =>
          message.ent === 'acn' &&
          (message.mysid == null || message.mysid === mysid);
        const matchesId = (message: any) => {
          if (message?.id === id) {
            return true;
          }
          if (message?.res?._id === id) {
            return true;
          }
          if (Array.isArray(message?.res)) {
            return message.res.some((org: any) => org?._id === id);
          }
          return false;
        };

        return this.organizationsService
          .getOrganizationDetails(
            {
              mysid,
              s: 'model',
              view: 'admin',
              id,
              cmd: 'get',
            },
            messageFilter
          )
          .pipe(
            filter((message: any) => {
              if (message?.sts?.sts === 'ERR') {
                return true;
              }
              if (!['get', 'query', 'fast_query'].includes(message?.op)) {
                return false;
              }
              return matchesId(message);
            }),
            take(1),
            map((response: any) => {
              if (response?.sts?.sts === 'ERR') {
                return OrganizationsActions.getOrganizationDetailsFailure({
                  error: response.sts,
                });
              }
              const org = Array.isArray(response?.res)
                ? response.res.find((item: any) => item?._id === id)
                : response.res;
              return OrganizationsActions.getOrganizationDetailsSuccess({
                res: org ?? response.res,
              });
            }),
            catchError(error =>
              of(OrganizationsActions.getOrganizationDetailsFailure({ error }))
            )
          );
      })
    )
  );

  getOrganizationLicenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.getOrganizationLicenses),
      mergeMap(() => this.organizationsService.getOrganizationLicenses()),
      map((response: any) =>
        OrganizationsActions.getOrganizationLicensesSuccess({
          res: response.res,
        })
      ),
      catchError(error =>
        of(OrganizationsActions.getOrganizationLicensesFailure({ error }))
      )
    )
  );

  updateOrganizationLicenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationLicenses),
      mergeMap(({ payload }) =>
        this.organizationsService.updateOrganizationLicense(payload)
      ),
      map((response: any) =>
        OrganizationsActions.updateOrganizationLicensesSuccess({
          res: response.res,
        })
      ),
      catchError(error =>
        of(OrganizationsActions.updateOrganizationLicensesFailure({ error }))
      )
    )
  );

  deleteOrganizationAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.deleteOrganizationAccount),
      mergeMap(({ acnId }) =>
        this.organizationsService
          .deleteOrganizationAccount(acnId)
          .pipe(map(() => acnId))
      ),
      map(acnId =>
        OrganizationsActions.deleteOrganizationAccountSuccess({ acnId })
      ),
      catchError(error =>
        of(OrganizationsActions.deleteOrganizationAccountFailure({ error }))
      )
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganization),
      mergeMap(({ id, testOrgFlg }) => {
        const payload = {
          ent: 'acn',
          id: id,
          op: 'upd',
          res: {
            testOrgFlg,
          },
        };
        this.organizationsService.updateOrganization(payload);
        return of(
          OrganizationsActions.updateOrganizationSuccess({
            res: { testOrgFlg },
          })
        );
      }),
      catchError(error =>
        of(OrganizationsActions.updateOrganizationFailure({ error }))
      )
    )
  );

  updateInvestorProfileVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateInvestorProfileVisibility),
      mergeMap(({ id, visibility }) =>
        this.organizationsService
          .updateInvestorProfileVisibility(id, visibility)
          .pipe(
            map(() =>
              OrganizationsActions.updateInvestorProfileVisibilitySuccess({
                res: { investorProfileVisibilityLevel: visibility },
              })
            ),
            catchError(error =>
              of(
                OrganizationsActions.updateInvestorProfileVisibilityFailure({
                  error,
                })
              )
            )
          )
      )
    )
  );

  updateFeatureStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateFeatureStage),
      mergeMap(({ id, featureStg }) =>
        this.organizationsService.updateFeatureStage(id, featureStg).pipe(
          map(() =>
            OrganizationsActions.updateFeatureStageSuccess({
              res: { featureStg },
            })
          ),
          catchError(error =>
            of(
              OrganizationsActions.updateFeatureStageFailure({
                error,
              })
            )
          )
        )
      )
    )
  );

  updateOrganizationUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationUser),
      mergeMap(({ userId, payload }) =>
        this.organizationsService.updateOrganizationUser(userId, payload).pipe(
          map(response =>
            OrganizationsActions.updateOrganizationUserSuccess({
              userId,
              updatedUser: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.updateOrganizationUserFailure({ error }))
          )
        )
      )
    )
  );

  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.resetUserPassword),
      mergeMap(({ userId }) =>
        this.organizationsService.resetUserPassword(userId).pipe(
          map(response =>
            OrganizationsActions.resetUserPasswordSuccess({
              userId,
              message: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.resetUserPasswordFailure({ error }))
          )
        )
      )
    )
  );

  resetUserPasswordLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.resetUserPasswordLink),
      mergeMap(({ userId }) =>
        this.organizationsService.resetUserPasswordLink(userId).pipe(
          map(response =>
            OrganizationsActions.resetUserPasswordLinkSuccess({
              userId,
              link: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.resetUserPasswordLinkFailure({ error }))
          )
        )
      )
    )
  );

  generateOnBehalfToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.generateOnBehalfToken),
      mergeMap(({ userId }) =>
        this.organizationsService.generateOnBehalfUrl(userId).pipe(
          map((token: string) =>
            OrganizationsActions.generateOnBehalfTokenSuccess({ userId, token })
          ),
          catchError(error =>
            of(OrganizationsActions.generateOnBehalfTokenFailure({ error }))
          )
        )
      )
    )
  );

  loginAsAnotherUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.loginAsAnotherUser),
      mergeMap(({ token }) =>
        this.organizationsService.loginAsAnotherUserByToken(token).pipe(
          map((response: any) =>
            OrganizationsActions.loginAsAnotherUserSuccess({
              userData: response?.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.loginAsAnotherUserFailure({ error }))
          )
        )
      )
    )
  );

  updateOrganizationName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationName),
      mergeMap(({ acnId, name }) =>
        this.organizationsService.updateOrganizationName(acnId, name).pipe(
          map(response =>
            OrganizationsActions.updateOrganizationNameSuccess({
              res: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.updateOrganizationNameFailure({ error }))
          )
        )
      )
    )
  );

  addOrganizationDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.addOrganizationDomain),
      mergeMap(({ acnId, domain }) =>
        this.organizationsService.addOrganizationDomain(acnId, domain).pipe(
          map(response =>
            OrganizationsActions.addOrganizationDomainSuccess({
              res: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.addOrganizationDomainFailure({ error }))
          )
        )
      )
    )
  );

  removeLoginBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.removeLoginBlock),
      mergeMap(({ email }) =>
        this.organizationsService.removeLoginBlock(email).pipe(
          map(response =>
            OrganizationsActions.removeLoginBlockSuccess({
              email,
              message: response?.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.removeLoginBlockFailure({ error }))
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private webSocketService: WebSocketService
  ) {}
}
